/**
 * DocumentPreviewModal — inline preview for a single document.
 *
 * Pipeline:
 *   - PDF: tries `apiClient.retail.documents.{id}/content` for a base64 blob or a
 *     short-lived signedUrl, and draws a PDF with pdf.js (PdfCanvasViewer, CHG-0097).
 *   - Image: same content fetch → `<img src=...>`.
 *   - Other: shows a friendly download CTA.
 *
 * When the content route is unavailable (501 / SP_UNAVAILABLE) the modal
 * surfaces a friendly placeholder rather than a stack trace, preserving the
 * "Open in browser" + Download options.
 *
 * Role gating: Manager+ sees a "Delete" button (calls onDelete). All viewers
 * see the metadata panel + audit-friendly captured-by line.
 */

import { useEffect, useMemo, useState } from 'react';
import { Download, ExternalLink, Trash2, X } from 'lucide-react';
import type { DocumentRef } from '@retail-ops/api-client';
import { ModalShell, Button, Banner, Pill, KVPair } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { DocumentsClient } from '../services/documentsClient';
import { DocumentTypeIcon } from './DocumentTypeIcon';
import { humanizeUploader, humanizeDocCode, formatRelativeDay } from './humanize';
import { CATEGORY_LABEL, formatBytes } from '@/hooks/useDocuments';
import { AuroraStatusPill } from './AuroraStatusPill';
import { PdfCanvasViewer } from '@/components/common/PdfCanvasViewer';

export interface DocumentPreviewModalProps {
  open: boolean;
  doc: DocumentRef | null;
  onClose: () => void;
  /** Manager+ only — when omitted, delete button hides. */
  onDelete?: (doc: DocumentRef) => void;
  /** Manager+ only — replace flow (re-upload over the same docCode slot). */
  onReplace?: (doc: DocumentRef) => void;
  canDelete?: boolean;
}

type PreviewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'inline-bytes'; dataUrl: string; contentType: string }
  | { kind: 'signed-url'; url: string; contentType: string; blob?: Blob }
  | { kind: 'unavailable'; reason: string };

export function DocumentPreviewModal({
  open,
  doc,
  onClose,
  onDelete,
  onReplace,
  canDelete = false,
}: DocumentPreviewModalProps): JSX.Element | null {
  const apiClient = useApiClient();
  const docsClient = useMemo(() => new DocumentsClient(apiClient), [apiClient]);
  const [state, setState] = useState<PreviewState>({ kind: 'idle' });

  useEffect(() => {
    if (!open || !doc) {
      setState({ kind: 'idle' });
      return;
    }
    if (!doc.previewable) {
      setState({ kind: 'unavailable', reason: 'Inline preview is available for PDFs and images only.' });
      return;
    }

    let cancelled = false;
    const ctrl = new AbortController();
    // Large documents come back as an object URL over a Blob we fetched with
    // auth (2026-09-05 streaming path). Those hold the bytes in memory until
    // revoked, so the URL is tracked and released when the modal closes or the
    // document changes — otherwise every preview would leak a whole file.
    let objectUrl: string | null = null;
    setState({ kind: 'loading' });

    void (async (): Promise<void> => {
      const result = await docsClient.previewUrl(doc.documentId, ctrl.signal);
      if (result.kind === 'signed-url' && result.url.startsWith('blob:')) {
        objectUrl = result.url;
      }
      if (cancelled) {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        return;
      }
      setState(result);
    })();

    return (): void => {
      cancelled = true;
      ctrl.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, doc, docsClient]);

  if (!doc) return null;

  const isPdf = doc.contentType === 'application/pdf';
  const isImage = doc.contentType.startsWith('image/');
  const previewSource =
    state.kind === 'inline-bytes' ? state.dataUrl :
    state.kind === 'signed-url' ? state.url :
    null;

  const docTypeLabel = doc.docCode ? humanizeDocCode(doc.docCode) : null;

  const footer = (
    <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', width: '100%' }}>
      {canDelete && onDelete && (
        <Button
          variant="ghost"
          size="sm"
          startIcon={<Trash2 size={14} />}
          onClick={() => onDelete(doc)}
          style={{ color: 'var(--pcb-red)' }}
        >
          Delete
        </Button>
      )}
      {canDelete && onReplace && (
        <Button variant="ghost" size="sm" onClick={() => onReplace(doc)}>
          Replace
        </Button>
      )}
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--sp-3)' }}>
        {previewSource && (
          <Button
            variant="ghost"
            size="sm"
            startIcon={<ExternalLink size={14} />}
            onClick={() => window.open(previewSource, '_blank', 'noopener,noreferrer')}
          >
            Open in new tab
          </Button>
        )}
        <Button
          variant="primary"
          size="sm"
          startIcon={<Download size={14} />}
          onClick={() => {
            if (previewSource) {
              const a = document.createElement('a');
              a.href = previewSource;
              a.download = doc.fileName;
              a.click();
            }
          }}
          disabled={!previewSource}
        >
          Download
        </Button>
        <Button variant="ghost" size="sm" startIcon={<X size={14} />} onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={doc.fileName}
      size="lg"
      footer={footer}
      ariaLabel={`Preview: ${doc.fileName}`}
    >
      <div className="rdl-preview">
        <div style={{ display: 'flex', gap: 'var(--sp-4)', alignItems: 'center' }}>
          <DocumentTypeIcon contentType={doc.contentType} size={20} />
          <div style={{ flex: 1, minWidth: 0 }}>
            {docTypeLabel ? (
              <Pill variant="info">{docTypeLabel}</Pill>
            ) : (
              <Pill variant="muted">{CATEGORY_LABEL[doc.category]}</Pill>
            )}
          </div>
          <AuroraStatusPill status={doc.auroraStatus} />
        </div>

        <section className="rdl-preview__meta" aria-label="Document details">
          <KVPair label="Customer" value={`CIF ${doc.cifId}`} />
          {doc.cartId && <KVPair label="Cart" value="In progress" />}
          <KVPair label="Captured" value={formatRelativeDay(doc.uploadedAt)} />
          <KVPair label="Captured by" value={humanizeUploader(doc.uploadedBy)} />
          <KVPair label="Size" value={formatBytes(doc.sizeBytes)} />
          <KVPair label="Category" value={CATEGORY_LABEL[doc.category]} />
        </section>

        <div className="rdl-preview__viewer" role="region" aria-label="Document preview">
          {state.kind === 'loading' && (
            <div className="rdl-preview__placeholder">Loading preview…</div>
          )}
          {state.kind === 'unavailable' && (
            <div style={{ padding: 'var(--sp-6)', maxWidth: 520 }}>
              <Banner tone="warn" title="Preview not available right now">
                <p style={{ margin: 0 }}>
                  {state.reason} Use the Download button above to fetch a copy, or open the file in
                  a new tab.
                </p>
              </Banner>
            </div>
          )}
          {previewSource && isPdf && (
            // pdf.js, not an <iframe>: iframe PDFs are blank on bank PCs (CHG-0097).
            <PdfCanvasViewer
              className="rdl-preview__iframe"
              source={state.kind === 'signed-url' && state.blob ? state.blob : previewSource}
              title={`Preview of ${doc.fileName}`}
            />
          )}
          {previewSource && isImage && (
            <img className="rdl-preview__image" src={previewSource} alt={doc.fileName} />
          )}
          {previewSource && !isPdf && !isImage && (
            <div className="rdl-preview__placeholder">
              <p>This file type opens outside the browser.</p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => window.open(previewSource, '_blank', 'noopener,noreferrer')}
              >
                Open in browser
              </Button>
            </div>
          )}
        </div>
      </div>
    </ModalShell>
  );
}
